import { type XYPosition } from '@xyflow/react';

/*
 * Positions along a polyline, as a fraction of its length. A label that the
 * user drags along a line saves its position as this fraction, so the label
 * stays at the same relative place when the line changes shape.
 */

const length = (a: XYPosition, b: XYPosition) =>
  Math.hypot(b.x - a.x, b.y - a.y);

/** Returns the point at `fraction` (0 to 1) of the length of `route`. */
export function getPointAtFraction(
  route: XYPosition[],
  fraction: number
): XYPosition {
  const total = route
    .slice(1)
    .reduce((sum, point, index) => sum + length(route[index]!, point), 0);
  let remaining = total * fraction;
  for (let index = 0; index < route.length - 1; index++) {
    const from = route[index]!;
    const to = route[index + 1]!;
    const segment = length(from, to);
    if (remaining <= segment && segment > 0) {
      const t = remaining / segment;
      return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
      };
    }
    remaining -= segment;
  }
  return route[route.length - 1]!;
}

/** Returns the fraction (0 to 1) of the length of `route` nearest to `point`. */
export function getNearestFraction(
  route: XYPosition[],
  point: XYPosition
): number {
  let best = { distance: Infinity, at: 0 };
  let walked = 0;
  for (let index = 0; index < route.length - 1; index++) {
    const from = route[index]!;
    const to = route[index + 1]!;
    const segment = length(from, to);
    const t =
      segment === 0
        ? 0
        : Math.max(
            0,
            Math.min(
              1,
              ((point.x - from.x) * (to.x - from.x) +
                (point.y - from.y) * (to.y - from.y)) /
                (segment * segment)
            )
          );
    const nearest = {
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t,
    };
    const distance = length(nearest, point);
    if (distance < best.distance) best = { distance, at: walked + segment * t };
    walked += segment;
  }
  return walked === 0 ? 0.5 : best.at / walked;
}
